import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { projectService } from '../../services/authService';
import { format } from 'date-fns';
import AutocompleteInput from '../../components/AutocompleteInput';

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [addMemberModal, setAddMemberModal] = useState(false);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState('member');
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [removingMember, setRemovingMember] = useState(null);
  const [togglingPermission, setTogglingPermission] = useState(null);
  const [requestModal, setRequestModal] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const fetchProject = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await projectService.getProject(projectId);
      setProject(data);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 404) {
        navigate('/app/projects');
        return;
      }
      setError(err.response?.data?.error || 'Failed to load project');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!memberEmail.trim()) return;

    setIsAddingMember(true);
    setError(null);
    try {
      await projectService.addMember(projectId, memberEmail.trim(), memberRole);
      setSuccessMessage(`${memberEmail} added to project`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setAddMemberModal(false);
      setMemberEmail('');
      setMemberRole('member');
      fetchProject();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add member');
    } finally {
      setIsAddingMember(false);
    }
  };

  // Fetch user suggestions for autocomplete
  const fetchUserSuggestions = useCallback(
    async (query) => {
      try {
        return await projectService.searchUsers(query, projectId);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('User search failed:', err);
        return [];
      }
    },
    [projectId]
  );

  const handleRemoveMember = async (memberId, email) => {
    if (!window.confirm(`Remove ${email} from this project?`)) return;

    setRemovingMember(memberId);
    setError(null);
    try {
      await projectService.removeMember(projectId, memberId);
      setSuccessMessage(`${email} removed from project`);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchProject();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove member');
    } finally {
      setRemovingMember(null);
    }
  };

  const handleToggleSimulationPermission = async (memberId, currentValue) => {
    setTogglingPermission(memberId);
    setError(null);
    try {
      await projectService.updateMemberPermissions(projectId, memberId, !currentValue);
      setSuccessMessage(`Simulation permission ${!currentValue ? 'granted' : 'revoked'}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchProject();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update permissions');
    } finally {
      setTogglingPermission(null);
    }
  };

  const handleRequestSimulation = async (e) => {
    e.preventDefault();
    setIsRequesting(true);
    setError(null);
    try {
      await projectService.requestSimulationAccess(projectId, requestReason);
      setSuccessMessage('Simulation access request submitted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setRequestModal(false);
      setRequestReason('');
      fetchProject();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setIsRequesting(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!window.confirm('Cancel the simulation access request?')) return;
    setIsCancelling(true);
    setError(null);
    try {
      await projectService.cancelSimulationRequest(projectId);
      setSuccessMessage('Request cancelled');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchProject();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel request');
    } finally {
      setIsCancelling(false);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: 'bg-purple-500/20 text-purple-400',
      admin: 'bg-primary-500/20 text-primary-400',
      member: 'bg-dark-700 text-dark-400',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[role] || styles.member}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  const canManageMembers = project?.your_role === 'owner' || project?.your_role === 'admin';
  const isOwner = project?.your_role === 'owner';

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6">
        <div className="card-dark p-8 text-center">
          <p className="text-dark-400">Project not found</p>
          <Link to="/app/projects" className="btn-primary mt-4">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/app/projects"
          className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-dark-100">{project.name}</h1>
          <div className="flex items-center gap-3 mt-1">
            {getRoleBadge(project.your_role)}
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              project.simulation_enabled
                ? 'bg-primary-500/20 text-primary-400'
                : 'bg-dark-600 text-dark-400'
            }`}>
              {project.simulation_enabled ? 'Simulations Enabled' : 'Simulations Disabled'}
            </span>
            <span className="text-dark-400 text-sm">
              Created {project.created_at ? format(new Date(project.created_at), 'MMM d, yyyy') : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-error-500/20 border border-error-500/50 rounded-lg text-error-400">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-4 bg-success-500/20 border border-success-500/50 rounded-lg text-success-400">
          {successMessage}
        </div>
      )}

      {/* Simulation Access Section (Owner only, when not enabled) */}
      {isOwner && !project.simulation_enabled && (
        <div className="card-dark mb-6">
          <div className="p-4 border-b border-dark-700">
            <h2 className="text-lg font-semibold text-dark-100">Simulation Access</h2>
          </div>
          <div className="p-4">
            {/* No request yet */}
            {!project.simulation_request_status && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-dark-300">Simulations are not enabled for this project.</p>
                  <p className="text-dark-400 text-sm mt-1">Request access from the administrator to enable simulations.</p>
                </div>
                <button
                  onClick={() => setRequestModal(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Request Access
                </button>
              </div>
            )}

            {/* Request pending */}
            {project.simulation_request_status === 'pending' && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                      Pending Approval
                    </span>
                  </div>
                  <p className="text-dark-400 text-sm mt-2">Your request is awaiting administrator approval.</p>
                  {project.simulation_request_reason && (
                    <p className="text-dark-500 text-sm mt-1">Reason: {project.simulation_request_reason}</p>
                  )}
                </div>
                <button
                  onClick={handleCancelRequest}
                  disabled={isCancelling}
                  className="btn-secondary flex items-center gap-2"
                >
                  {isCancelling ? (
                    <div className="w-4 h-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  Cancel Request
                </button>
              </div>
            )}

            {/* Request rejected */}
            {project.simulation_request_status === 'rejected' && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-error-500/20 text-error-400">
                      Request Rejected
                    </span>
                  </div>
                  {project.simulation_rejection_reason && (
                    <p className="text-dark-400 text-sm mt-2">Reason: {project.simulation_rejection_reason}</p>
                  )}
                  <p className="text-dark-500 text-sm mt-1">You can submit a new request.</p>
                </div>
                <button
                  onClick={() => setRequestModal(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Request Again
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members Section */}
      <div className="card-dark">
        <div className="p-4 border-b border-dark-700 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-dark-100">
            Team Members ({project.members?.length || 0})
          </h2>
          {canManageMembers && (
            <button
              onClick={() => setAddMemberModal(true)}
              className="btn-primary text-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Member
            </button>
          )}
        </div>

        <div className="divide-y divide-dark-700">
          {project.members?.map((member) => (
            <div key={member.id} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-dark-600 rounded-full flex items-center justify-center">
                  <span className="text-dark-300 font-medium">
                    {member.email.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-dark-100 font-medium">{member.email}</p>
                  <p className="text-dark-400 text-sm">
                    Joined {member.joined_at ? format(new Date(member.joined_at), 'MMM d, yyyy') : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {getRoleBadge(member.role)}
                {/* Simulation permission toggle (owner only, not for owner member) */}
                {isOwner && member.role !== 'owner' && (
                  <button
                    onClick={() => handleToggleSimulationPermission(member.id, member.can_launch_simulations)}
                    disabled={togglingPermission === member.id}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                      member.can_launch_simulations
                        ? 'bg-success-500/20 text-success-400 hover:bg-success-500/30'
                        : 'bg-dark-600 text-dark-400 hover:bg-dark-500'
                    }`}
                    title={member.can_launch_simulations ? 'Click to revoke launch permission' : 'Click to grant launch permission'}
                  >
                    {togglingPermission === member.id ? (
                      <div className="w-3 h-3 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    )}
                    {member.can_launch_simulations ? 'Can Launch' : 'No Launch'}
                  </button>
                )}
                {/* Show badge for non-owners viewing permissions */}
                {!isOwner && member.role !== 'owner' && (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    member.can_launch_simulations
                      ? 'bg-success-500/20 text-success-400'
                      : 'bg-dark-600 text-dark-400'
                  }`}>
                    {member.can_launch_simulations ? 'Can Launch' : 'Read Only'}
                  </span>
                )}
                {/* Owner always has launch permission */}
                {member.role === 'owner' && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-success-500/20 text-success-400">
                    Can Launch
                  </span>
                )}
                {canManageMembers && member.role !== 'owner' && (
                  <button
                    onClick={() => handleRemoveMember(member.id, member.email)}
                    disabled={removingMember === member.id}
                    className="p-2 text-dark-400 hover:text-error-400 hover:bg-error-500/10 rounded-lg transition-colors disabled:opacity-50"
                    title="Remove member"
                  >
                    {removingMember === member.id ? (
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-error-400 border-t-transparent"></div>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Member Modal */}
      {addMemberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold text-dark-100 mb-4">Add Team Member</h2>
            <p className="text-dark-400 text-sm mb-4">
              The user must already be registered in the system.
            </p>
            <form onSubmit={handleAddMember}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Email Address
                </label>
                <AutocompleteInput
                  value={memberEmail}
                  onChange={setMemberEmail}
                  onSelect={(user) => setMemberEmail(user.email)}
                  fetchSuggestions={fetchUserSuggestions}
                  placeholder="Type to search users..."
                  minChars={2}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Role
                </label>
                <select
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value)}
                  className="input-dark w-full"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="text-dark-500 text-xs mt-1">
                  Admins can add and remove members.
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setAddMemberModal(false);
                    setMemberEmail('');
                    setMemberRole('member');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!memberEmail.trim() || isAddingMember}
                  className="btn-primary disabled:opacity-50"
                >
                  {isAddingMember ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Request Simulation Access Modal */}
      {requestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold text-dark-100 mb-4">Request Simulation Access</h2>
            <p className="text-dark-400 text-sm mb-4">
              Explain why your project needs simulation capabilities. The administrator will review your request.
            </p>
            <form onSubmit={handleRequestSimulation}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Reason (optional)
                </label>
                <textarea
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  placeholder="Describe your use case and why you need simulation access..."
                  className="input-dark w-full h-24 resize-none"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setRequestModal(false);
                    setRequestReason('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isRequesting}
                  className="btn-primary disabled:opacity-50"
                >
                  {isRequesting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetailPage;
